import { ExperimentOutlined } from '@ant-design/icons'

import { Form, Grid, Input, Modal, Select, Spin, message } from 'antd'

import { useEffect, useMemo, useState } from 'react'

import {
    apiGetTestCaseDetail,
    createTestCase,
    updateTestCase,
} from '../../../../../redux/testRun/testRun.service'

import {
    createFetchSprints,
    useAsyncSelect,
} from '../../../../../utils/asyncSelectHelpers'

import AsyncTagField from '../../../../shared/AsyncTagField'

import { resolveTestRunProjectId } from '../../../../shared/TestRunTabs'

import { isValidArray } from '../../../../../utils/helpers'

import styles from './AddTestCaseModal.module.css'

const { TextArea } = Input

function mergeSelectOptions(...groups) {
    const map = new Map()
    groups.flat().forEach((opt) => {
        if (opt?.value) map.set(opt.value, opt)
    })
    return Array.from(map.values())
}

const AddTestCaseModal = ({
    isOpen = false,
    setOpen = () => {},
    onSuccess = () => {},
    projectId: projectIdProp,
    testCaseId = null,
}) => {
    const [form] = Form.useForm()
    const screens = Grid.useBreakpoint()
    const modalWidth = screens.lg ? 780 : screens.md ? 680 : '94%'
    const isEditMode = Boolean(testCaseId)

    const [submitting, setSubmitting] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [sprintSeed, setSprintSeed] = useState([])

    const projectId = resolveTestRunProjectId(projectIdProp)
    const fetchSprintsForProject = useMemo(
        () => createFetchSprints(projectId),
        [projectId]
    )
    const sprintSelect = useAsyncSelect(fetchSprintsForProject)

    const sprintOptions = useMemo(
        () => mergeSelectOptions(sprintSeed, sprintSelect.options),
        [sprintSeed, sprintSelect.options]
    )



    useEffect(() => {

        if (!isOpen) return



        if (!isEditMode) {

            form.resetFields()
            setSprintSeed([])
            setDetailLoading(false)
            return
        }

        let cancelled = false
        ;(async () => {
            setDetailLoading(true)
            form.resetFields()
            setSprintSeed([])
            try {
                    const res = await apiGetTestCaseDetail(testCaseId)
                    const detail = res?.data
                    if (cancelled) return

                    if (!detail?._id) {

                        message.error('Failed to load test case details')

                        return

                    }



                    if (detail.sprint?._id) {
                        setSprintSeed([
                            {
                                value: detail.sprint._id,
                                label: detail.sprint.title || 'Sprint',
                            },
                        ])
                    }

                    form.setFieldsValue({
                        title: detail.title || '',
                        description: detail.description || '',
                        preconditions: detail.preconditions || '',
                        testData: detail.testData || '',
                        steps: detail.steps || '',
                        expectedResult: detail.expectedResult || '',
                        sprint: detail.sprint?._id,
                        tags: isValidArray(detail.tags)
                            ? detail.tags.map((tag) => ({
                                  value: tag._id,
                                  label: tag.name,
                              }))
                            : [],
                    })

                } catch {

                    if (!cancelled) {

                        message.error('Failed to load test case details')

                    }

                } finally {

                    if (!cancelled) setDetailLoading(false)

                }

            })()



        return () => {

            cancelled = true

        }

    }, [isOpen, isEditMode, testCaseId, form])



    const handleClose = () => {

        if (submitting) return

        form.resetFields()
        setSprintSeed([])
        setOpen(false)
    }

    const buildPayloadFields = (values) => {
        const sprintId = values?.sprint?.value || values?.sprint
        const tagIds =
            values?.tags?.map((t) => t?.value || t).filter(Boolean) || []

        return {
            title: values.title?.trim(),
            description: values.description || '',
            preconditions: values.preconditions || '',
            testData: values.testData || '',
            steps: values.steps || '',
            expectedResult: values.expectedResult || '',
            sprint: sprintId,
            tags: tagIds,
        }
    }



    const handleSubmit = async (values) => {

        if (!projectId) {

            message.error('Project is required')

            return

        }



        setSubmitting(true)

        try {

            const fields = buildPayloadFields(values)



            if (isEditMode) {

                const res = await updateTestCase({

                    id: testCaseId,

                    data: fields,

                })

                if (res?.data?.success) {

                    message.success(

                        res?.data?.message || 'Test case updated successfully'

                    )

                    handleClose()

                    onSuccess()

                } else {

                    message.error(

                        res?.data?.message || 'Failed to update test case'

                    )

                }

                return

            }



            const res = await createTestCase({

                ...fields,

                project: projectId,

            })

            if (res?.data?.success) {

                message.success('Test case added successfully')

                handleClose()

                onSuccess()

            } else {

                message.error(res?.data?.message || 'Failed to add test case')

            }

        } catch {

            message.error(

                isEditMode

                    ? 'Failed to update test case'

                    : 'Failed to add test case'

            )

        } finally {

            setSubmitting(false)

        }

    }



    return (

        <Modal

            className={styles.modal}

            open={isOpen}

            onCancel={handleClose}

            destroyOnClose

            width={modalWidth}

            centered

            maskClosable={!submitting && !detailLoading}

            closable={!submitting && !detailLoading}

            title={

                <div className={styles.header}>

                    <div className={styles.headerIcon}>

                        <ExperimentOutlined />

                    </div>

                    <div className={styles.headerText}>

                        <h2 className={styles.modalTitle}>

                            {isEditMode ? 'Edit Test Case' : 'Add Test Case'}

                        </h2>

                        <p className={styles.subtitle}>

                            {isEditMode

                                ? 'Update the test scenario, steps, and organization for this case.'

                                : 'Define the test scenario, steps, and link it to a sprint before adding it to your project.'}

                        </p>

                    </div>

                </div>

            }

            footer={

                <div className={styles.footerBar}>

                    <span className={styles.footerHint}>

                        Title, Tag and sprint are required

                    </span>

                    <div className={styles.footerActions}>

                        <button

                            type="button"

                            className={styles.cancelButton}

                            onClick={handleClose}

                            disabled={submitting || detailLoading}

                        >

                            Cancel

                        </button>

                        <button

                            type="button"

                            disabled={submitting || detailLoading}

                            onClick={() => form.submit()}

                            className={`${styles.submitButton} ${submitting ? styles.submitButtonDisabled : ''

                                }`}

                        >

                            {submitting

                                ? isEditMode

                                    ? 'Saving...'

                                    : 'Adding...'

                                : isEditMode

                                    ? 'Save Changes'

                                    : 'Add Test Case'}

                        </button>

                    </div>

                </div>

            }

        >

            {detailLoading ? (

                <div className={styles.loadingWrap}>

                    <Spin />

                </div>

            ) : (

                <Form

                    form={form}

                    layout="vertical"

                    className={styles.form}

                    onFinish={handleSubmit}

                    requiredMark={false}

                    initialValues={{ tags: [] }}

                >

                    <div className={styles.sections}>

                        <section className={styles.sectionCard}>

                            <div className={styles.sectionCardHeader}>

                                <p className={styles.sectionEyebrow}>Step 1</p>

                                <h3 className={styles.sectionTitle}>Overview</h3>

                            </div>

                            <div className={styles.formGrid}>

                                <Form.Item

                                    label="Title"

                                    name="title"

                                    className={styles.formGridFull}

                                    rules={[

                                        {

                                            required: true,

                                            message: 'Title is required',

                                        },

                                        {

                                            max: 200,

                                            message: 'Max 200 characters',

                                        },

                                    ]}

                                >

                                    <Input

                                        placeholder="e.g. Verify login with valid credentials"

                                        allowClear

                                    />

                                </Form.Item>

                                <Form.Item

                                    label="Description"

                                    name="description"

                                    className={styles.formGridFull}

                                >

                                    <TextArea
                                        autoSize={{ minRows: 2, maxRows: 12 }}
                                        placeholder="Brief summary of what this test case covers…"
                                    />

                                </Form.Item>

                            </div>

                        </section>



                        <section className={styles.sectionCard}>

                            <div className={styles.sectionCardHeader}>

                                <p className={styles.sectionEyebrow}>Step 2</p>

                                <h3 className={styles.sectionTitle}>

                                    Test specification

                                </h3>

                            </div>

                            <div className={styles.formGrid}>

                                <Form.Item

                                    label="Preconditions"

                                    name="preconditions"

                                    className={styles.formGridFull}

                                >

                                    <TextArea
                                        className={styles.textareaCompact}
                                        autoSize={{ minRows: 2, maxRows: 12 }}
                                        placeholder="What must be true before running this test?"
                                    />

                                </Form.Item>

                                <Form.Item label="Test data" name="testData">

                                    <TextArea
                                        className={styles.textareaCompact}
                                        autoSize={{ minRows: 2, maxRows: 10 }}
                                        placeholder="Sample inputs or test data"
                                    />

                                </Form.Item>

                                <Form.Item label="Steps" name="steps">

                                    <TextArea
                                        autoSize={{ minRows: 3, maxRows: 14 }}
                                        placeholder={
                                            '1. Open app\n2. Enter credentials\n3. Click login'
                                        }
                                    />

                                </Form.Item>

                                <Form.Item

                                    label="Expected result"

                                    name="expectedResult"

                                >

                                    <TextArea
                                        className={styles.textareaCompact}
                                        autoSize={{ minRows: 2, maxRows: 10 }}
                                        placeholder="What should happen when the test passes?"
                                    />

                                </Form.Item>

                            </div>

                        </section>



                        <section className={styles.sectionCard}>

                            <div className={styles.sectionCardHeader}>

                                <p className={styles.sectionEyebrow}>Step 3</p>

                                <h3 className={styles.sectionTitle}>

                                    Organization

                                </h3>

                            </div>

                            <div className={styles.formGrid}>

                                <Form.Item

                                    label="Sprint"

                                    name="sprint"

                                    rules={[

                                        {

                                            required: true,

                                            message: 'Sprint is required',

                                        },

                                    ]}

                                >

                                    <Select

                                        allowClear

                                        showSearch

                                        placeholder="Select sprint"

                                        filterOption={false}

                                        onSearch={sprintSelect.onSearch}

                                        onOpenChange={

                                            sprintSelect.onDropdownVisibleChange

                                        }

                                        onPopupScroll={

                                            sprintSelect.onPopupScroll

                                        }

                                        options={sprintOptions}

                                        loading={sprintSelect.loading}

                                        notFoundContent={

                                            sprintSelect.notFoundContent

                                        }

                                    />

                                </Form.Item>

                                <div className={styles.formGridFull}>
                                    <AsyncTagField
                                        form={form}
                                        creatable
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please select at least one tag',
                                            },
                                        ]}
                                    />
                                </div>

                            </div>

                        </section>

                    </div>

                </Form>

            )}

        </Modal>

    )

}



export default AddTestCaseModal


